import {Stack, TextInput} from '@mantine/core';
import React from 'react';

import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {Optional} from 'util/Types';

const MAX_NAME_LENGTH = 255;

export interface CreatePartFormProps {
  name: string;
  folderId: Optional<string>;
  partLinkFieldDbId: number | null;
  partLinkFieldOptions: Array<OptionData> | null;
  onNameChange: (name: string) => void;
  onFolderIdChange: (folderId: string) => void;
  onPartLinkFieldDbIdChange: (partLinkFieldDbId: number | null) => void;
}

export function getCreateFormValidation({name, folderId}: {name: string; folderId: Optional<string>}): {
  isValid: boolean;
  nameIsTooLong: boolean;
  nameErrorMessage?: string;
} {
  const nameOrFolderMissing = !folderId || name.trim().length === 0;
  const nameIsTooLong = name.length > MAX_NAME_LENGTH;
  const nameErrorMessage = nameIsTooLong
    ? `Names can be at most 255 characters long (currently ${name.length})`
    : undefined;

  return {
    isValid: !nameOrFolderMissing && !nameIsTooLong,
    nameIsTooLong,
    nameErrorMessage,
  };
}

export default function CreatePartForm({
  name,
  folderId,
  partLinkFieldDbId,
  partLinkFieldOptions,
  onNameChange,
  onFolderIdChange,
  onPartLinkFieldDbIdChange,
}: CreatePartFormProps): JSX.Element {
  const {nameErrorMessage} = getCreateFormValidation({name, folderId});

  return (
    <Stack spacing="0">
      <FormGroup>
        <FieldLabel label="Part name" isRequired={true} htmlFor="name" />
        <TextInput
          id="name"
          autoFocus={true}
          placeholder="Name"
          onChange={(e) => {
            onNameChange(e.target.value);
          }}
          value={name}
          error={nameErrorMessage}
          onFocus={(e) => {
            const input = e.target;
            input.setSelectionRange(0, input.value.length);
          }}
          aria-label="Part name"
        />
      </FormGroup>

      {partLinkFieldOptions != null && partLinkFieldOptions.length > 0 && (
        <FormGroup>
          <FieldLabel label="Part link field" isRequired={false} />
          <SelectDropdown
            displayName={
              partLinkFieldOptions.find((option) => option.value === partLinkFieldDbId)?.name ??
              'Select a part link field'
            }
            menu={
              <SearchableMenu
                width={Menu.WIDTHS.FIT_CONTENT}
                onSelect={(value: number | null) => {
                  onPartLinkFieldDbIdChange(value);
                }}
                optionDatas={partLinkFieldOptions}
                selectedValue={partLinkFieldDbId}
              />
            }
          />
        </FormGroup>
      )}

      <FormGroup>
        <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
        <FolderSelector
          buttonSize={ButtonSize.MEDIUM}
          enableRedesign={true}
          showInventory={true}
          value={folderId}
          onChange={(e) => {
            onFolderIdChange(e.target.value);
          }}
          unsetOnFetchFail={true}
          allowNone={false}
        />
      </FormGroup>
    </Stack>
  );
}
